import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaTrophy, FaMedal, FaCalendarAlt, FaChartBar } from "react-icons/fa";
import ClassicLeague from "./ClassicLeague";
import The100 from "./The100";
import GWwinner from "./GWwinner";
import MonthlyWinner from "./MonthlyWinner";
import LeagueStats from "./LeagueStats";

const COMPETITIONS = {
  s16: {
    key: "s16",
    title: "S16",
    subtitle: "FPL Nepal Season 16",
    leagueId: 136323,
    gwWinnerCount: 3,
    route: "/s16",
    accent: "from-cyan-500 via-blue-600 to-violet-700",
  },
  the100: {
    key: "the100",
    title: "THE 100",
    subtitle: "FPL Nepal THE 100",
    leagueId: 772976,
    gwWinnerCount: 1,
    route: "/the-100",
    accent: "from-emerald-500 via-teal-600 to-cyan-700",
  },
};

const TABS = [
  { key: "standings", label: "Standings", icon: FaTrophy },
  { key: "gw", label: "Gameweek Winners", icon: FaMedal },
  { key: "month", label: "Manager of the Month", icon: FaCalendarAlt },
  { key: "stats", label: "League Stats", icon: FaChartBar },
];

export default function LeagueSeasonHub({ competition = "s16" }) {
  const config = COMPETITIONS[competition] || COMPETITIONS.s16;
  const location = useLocation();
  const navigate = useNavigate();
  const availableTabs = useMemo(
    () => (config.key === "the100" ? TABS.filter((item) => item.key !== "month") : TABS),
    [config.key]
  );
  const queryTab = new URLSearchParams(location.search).get("tab");
  const initialTab = availableTabs.some((t) => t.key === queryTab) ? queryTab : "standings";
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    const requested = new URLSearchParams(location.search).get("tab");
    const next = availableTabs.some((item) => item.key === requested) ? requested : "standings";
    setTab(next);
    if (requested && requested !== next) {
      navigate(`${config.route}?tab=${next}`, { replace: true });
    }
  }, [availableTabs, config.route, location.search, navigate]);

  const switchCompetition = (nextConfig) => {
    const nextTab = nextConfig.key === "the100" && tab === "month" ? "standings" : tab;
    navigate(`${nextConfig.route}?tab=${nextTab}`);
  };

  const setActiveTab = (next) => {
    setTab(next);
    navigate(`${config.route}?tab=${next}`, { replace: true });
  };

  const content = useMemo(() => {
    if (tab === "gw") {
      return (
        <GWwinner
          leagueId={config.leagueId}
          winnerCount={config.gwWinnerCount}
          title={config.title}
          embedded
        />
      );
    }
    if (tab === "month" && config.key === "s16") {
      return <MonthlyWinner leagueId={config.leagueId} title={config.title} embedded />;
    }
    if (tab === "stats") {
      return <LeagueStats leagueId={config.leagueId} title={config.title} embedded />;
    }
    return config.key === "the100" ? (
      <The100 leagueId={config.leagueId} embedded />
    ) : (
      <ClassicLeague leagueId={config.leagueId} embedded />
    );
  }, [tab, config]);

  return (
    <div className="max-w-[1500px] mx-auto">
      <section className={`rounded-3xl overflow-hidden bg-gradient-to-r ${config.accent} text-white shadow-xl mb-5`}>
        <div className="px-5 sm:px-7 py-6 sm:py-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
          <div>
            <div className="text-xs font-black tracking-[0.22em] uppercase text-white/70">FPL Nepal · 2026/27</div>
            <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tight">{config.title}</h1>
            <p className="mt-2 text-white/80 font-semibold">{config.subtitle}</p>
            <p className="mt-1 text-sm text-white/65">
              {config.key === "s16"
                ? "Standings · 3 Gameweek winners · Manager of the Month · League Stats"
                : "Standings · 1 Gameweek winner · League Stats"}
            </p>
          </div>

          <div className="inline-flex rounded-2xl bg-white/12 p-1.5 backdrop-blur ring-1 ring-white/15 self-start lg:self-auto">
            {Object.values(COMPETITIONS).map((item) => (
              <button
                key={item.key}
                onClick={() => switchCompetition(item)}
                className={`px-5 py-2.5 rounded-xl text-sm font-black transition ${
                  item.key === config.key ? "bg-white text-slate-950 shadow-lg" : "text-white/80 hover:bg-white/10 hover:text-white"
                }`}
              >
                {item.title}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/80 p-1.5 mb-5 overflow-x-auto">
        <div className="flex min-w-max sm:min-w-0 gap-1.5">
          {availableTabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`inline-flex items-center justify-center gap-2 px-4 sm:px-5 py-3 rounded-xl text-sm font-extrabold transition ${
                tab === key
                  ? "bg-slate-950 text-white shadow-md"
                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-950"
              }`}
            >
              <Icon className="text-xs" />
              {label}
            </button>
          ))}
        </div>
      </div>

      <section className="league-season-content">{content}</section>
    </div>
  );
}
